// @flow

import { get, uniq } from 'lodash';
import logger from 'Logger';
import { resolve } from 'redux-optimist-promise';
import { FETCH_BOOKING_CART } from 'Components/widgets/cart/bookingCart.reducer';

const initialState = {
  completedIntro: false,
  resort: null,
  stayWithVendor: null,
  selectableRooms: [],
  selectableRoomsUnitPrice: null,
  currentSelectedRoomIndex: null,
  currentSelectableRoomIds: [],
  currentSelectableFloorIds: [],
  currentSelectableBuildingIds: [],
  filteredBuildings: [],
  selectedBuildingId: null,
  filteredFloors: [],
  selectedFloorId: null,
  filteredRoomsForCurrentFloor: [],
  filteredRooms: [],
  selectedRooms: [],
  numRoomsLeftToPick: null,
  highlightedRoom: null,
  isLoadingResort: false,
  isLoadingSelectableRooms: false,
  resortPYRMail: null,
  errors: null,
  roomsInCart: [],
};

export const LOAD_RESORT = 'LOAD_RESORT';
export const LOADING_ERROR = 'LOADING_ERROR';
export const LOADING_RESORT = 'LOADING_RESORT';
export const LOAD_SELECTABLE_ROOMS = 'LOAD_SELECTABLE_ROOMS';
export const LOADING_SELECTABLE_ROOMS = 'LOADING_SELECTABLE_ROOMS';
export const COMPLETE_INTRO = 'COMPLETE_INTRO';
export const SET_ROOM_SELECTION_INDEX = 'SET_ROOM_SELECTION_INDEX';
export const SELECT_BUILDING = 'SELECT_BUILDING';
export const SELECT_FLOOR = 'SELECT_FLOOR';
export const SELECT_ROOM = 'SELECT_ROOM';
export const HIGHLIGHT_ROOM = 'HIGHLIGHT_ROOM';
export const CUSTOMER_FIDELITY_STATUS = 'CUSTOMER_FIDELITY_STATUS';

function getResortFeatures(state) {
  return (state.resort && state.resort.rooms_geo_json.features) || [];
}

export default function reducer(state: ReduxState = initialState, action: Action<*>) {
  switch (action.type) {
    case CUSTOMER_FIDELITY_STATUS: {
      return {
        ...state,
        fidelityStatus: action.payload,
      };
    }
    case LOADING_RESORT: {
      return {
        ...state,
        isLoadingResort: true,
      };
    }
    case LOADING_ERROR: {
      return {
        ...state,
        isLoadingResort: false,
      };
    }
    case LOAD_RESORT: {
      return {
        ...state,
        resort: action.payload,
        isLoadingResort: false,
      };
    }
    case LOADING_SELECTABLE_ROOMS: {
      return {
        ...state,
        isLoadingSelectableRooms: true,
      };
    }
    case LOAD_SELECTABLE_ROOMS: {
      const {
        payload: { stayWithVendor, roomSelectionServices, errors, resortPYRMail },
      } = action;

      const selectableRooms = (roomSelectionServices || []).reduce((acc, roomSelectionService) => {
        return [
          ...acc,
          ...new Array(roomSelectionService.quantity).fill({
            ...roomSelectionService.accommodations[0],
            _serviceId: roomSelectionService.id,
            _unitPrice: roomSelectionService.unit_price,
          }),
        ];
      }, []);

      if (state.roomsInCart.length > 0) {
        const selectedRooms = state.roomsInCart.map(roomId =>
          getResortFeatures(state).find(geoRoom => geoRoom.properties.id === roomId),
        );

        return {
          ...state,
          isLoadingSelectableRooms: false,
          stayWithVendor,
          selectableRooms,
          errors,
          resortPYRMail,
          selectedRooms,
          numRoomsLeftToPick: selectableRooms.length - selectedRooms.length,
          highlightedRoom: null,
        };
      }

      return {
        ...state,
        isLoadingSelectableRooms: false,
        stayWithVendor,
        selectableRooms,
        numRoomsLeftToPick: selectableRooms.length,
        errors,
        resortPYRMail,
      };
    }
    case COMPLETE_INTRO: {
      return {
        ...state,
        completedIntro: true,
      };
    }
    case SET_ROOM_SELECTION_INDEX: {
      const {
        payload: { roomIndex },
      } = action;
      const features = getResortFeatures(state);
      if (typeof roomIndex === 'undefined' || roomIndex === null) {
        return {
          ...state,
          currentSelectedRoomIndex: null,
          currentSelectableRoomIds: [],
          currentSelectableFloorIds: [],
          currentSelectableBuildingIds: [],
          filteredBuildings: [],
          selectedBuildingId: null,
          filteredFloors: [],
          selectedFloorId: null,
          filteredRoomsForCurrentFloor: [],
          filteredRooms: [],
        };
      }

      const isRoomSelected = roomId =>
        state.selectedRooms.some(
          selectedRoomItem => selectedRoomItem && selectedRoomItem.properties.id === roomId,
        );

      // collect all ids for the current selectable room
      const currentSelectableRoomIds = state.selectableRooms[roomIndex].rooms
        .map(room => room.id)
        .filter(roomId => !isRoomSelected(roomId));

      // collect properties and geometries for those rooms
      const currentSelectableRooms = features.filter(
        room => currentSelectableRoomIds.indexOf(room.properties.id) > -1,
      );

      // collect properties and geometries for those rooms
      // $FlowFixMe
      const currentSelectableFloorIds = uniq(
        currentSelectableRooms.map(room => room.properties.floor_id),
      );

      // collect all building ids that contain those floors
      // $FlowFixMe
      const currentSelectableBuildingIds = uniq(
        ((state.resort && state.resort.floors) || [])
          .filter(floor => currentSelectableFloorIds.indexOf(floor.id) > -1)
          .map(floor => floor.building_id),
      );

      const filteredBuildings = ((state.resort && state.resort.buildings) || []).filter(
        building => currentSelectableBuildingIds.indexOf(building.id) > -1,
      );

      if (filteredBuildings.length === 0) {
        logger.warn(
          'SET_ROOM_SELECTION_INDEX no building found for building.id',
          'currentSelectableBuildingIds:',
          currentSelectableBuildingIds,
          'state.resort.buildings:',
          state.resort && state.resort.buildings,
        );
        logger.warn(
          'currentSelectableBuildingIds:',
          currentSelectableBuildingIds,
          'currentSelectableFloorIds:',
          currentSelectableFloorIds,
          'currentSelectableRooms:',
          currentSelectableRooms,
          'currentSelectableRoomIds:',
          currentSelectableRoomIds,
        );
      }

      const filteredRooms = features.filter(
        room => currentSelectableRoomIds.indexOf(room.properties.id) > -1,
      );

      return {
        ...state,
        currentSelectedRoomIndex: roomIndex,
        currentSelectableRoomIds,
        currentSelectableFloorIds,
        currentSelectableBuildingIds,
        filteredBuildings,
        selectedBuildingId: null,
        filteredFloors: [],
        selectedFloorId: null,
        filteredRoomsForCurrentFloor: [],
        filteredRooms,
      };
    }
    case SELECT_BUILDING: {
      const {
        payload: { buildingId },
      } = action;
      const filteredFloors = ((state.resort && state.resort.floors) || []).filter(
        floor =>
          state.currentSelectableFloorIds.indexOf(floor.id) > -1 &&
          floor.building_id === buildingId,
      );

      return {
        ...state,
        selectedBuildingId: buildingId,
        filteredFloors: filteredFloors,
      };
    }
    case SELECT_FLOOR: {
      const {
        payload: { floorId },
      } = action;

      const filteredRoomsForCurrentFloor = getResortFeatures(state).filter(
        room =>
          state.currentSelectableRoomIds.indexOf(room.properties.id) > -1 &&
          room.properties.floor_id === floorId,
      );

      return {
        ...state,
        selectedFloorId: floorId,
        filteredRoomsForCurrentFloor: filteredRoomsForCurrentFloor,
      };
    }
    case SELECT_ROOM: {
      const {
        payload: { roomId },
      } = action;
      const selectedRoom = {
        ...getResortFeatures(state).find(geoRoom => geoRoom.properties.id === roomId),
      };
      const selectedRooms = [...state.selectedRooms];

      if (state.currentSelectedRoomIndex || state.currentSelectedRoomIndex === 0) {
        selectedRooms[state.currentSelectedRoomIndex] = selectedRoom;
      }

      return {
        ...state,
        selectedRooms,
        numRoomsLeftToPick:
          state.selectableRooms.length - selectedRooms.filter(r => r !== undefined).length,
        highlightedRoom: null,
      };
    }
    case HIGHLIGHT_ROOM: {
      const {
        payload: { roomId },
      } = action;
      const highlightedRoom =
        roomId === undefined
          ? null
          : { ...getResortFeatures(state).find(room => room.properties.id === roomId) };

      return {
        ...state,
        highlightedRoom: highlightedRoom,
      };
    }

    case resolve(FETCH_BOOKING_CART): {
      const { payload } = action;

      const roomIds = get(payload, 'userBooking.cart.pickRoom', []).reduce(
        (acc, srv) =>
          acc.concat(
            srv.accommodations.reduce((r, a) => r.concat(a.rooms.map(({ id }) => id)), []),
          ),
        [],
      );

      if (getResortFeatures(state).length === 0) {
        return {
          ...state,
          roomsInCart: roomIds,
        };
      } else {
        const selectedRooms = roomIds.map(roomId =>
          getResortFeatures(state).find(geoRoom => geoRoom.properties.id === roomId),
        );
        return {
          ...state,
          selectedRooms,
          numRoomsLeftToPick: state.selectableRooms.length - selectedRooms.length,
          highlightedRoom: null,
        };
      }
    }

    default: {
      return state;
    }
  }
}
